// Wireframe dlgDelGen — confirmação de remoção de modelo, markup LITERAL
// (varredura v2 §5.6-C3): WfModalShell + header com lixeira vermelha +
// "Tem certeza que deseja excluir o modelo <nome>?" + .btn ghost/danger.
// Soft-delete: DELETE /v1/llm-models/:id marca status='deprecated'.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api, type LlmModel } from '@/lib/api';
import { WfModalShell } from '@/components/settings/WfModalShell';
import { useToast } from '@/components/ui/Toast';
import { mutationErrorMessage } from './modelHelpers';

interface ModelDeleteDialogProps {
  model: LlmModel | null;
  onClose: () => void;
}

export function ModelDeleteDialog({ model, onClose }: ModelDeleteDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) => api.deleteLlmModel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-catalog'] });
      toast({ message: `Modelo ${model?.model_key} removido (marcado como deprecated).`, type: 'success' });
      onClose();
    },
    onError: (err) => {
      toast({ message: mutationErrorMessage(err, 'remover o modelo'), type: 'error' });
    },
  });

  if (!model) return null;

  return (
    <WfModalShell
      open
      onClose={onClose}
      icon={<Trash2 className="ic" style={{ color: 'var(--wf-danger-text)' }} aria-hidden />}
      title="Confirmar exclusão"
      footer={
        <>
          <button type="button" className="wf-btn wf-btn-ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </button>
          <button
            type="button"
            className="wf-btn wf-btn-danger"
            onClick={() => mutation.mutate(model.id)}
            disabled={mutation.isPending}
          >
            <Trash2 aria-hidden />
            {mutation.isPending ? 'Excluindo…' : 'Excluir'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: '.92rem', lineHeight: 1.5, margin: 0 }}>
        Tem certeza que deseja excluir <b style={{ color: 'var(--wf-text2)' }}>o modelo</b>{' '}
        <b style={{ fontFamily: 'var(--wf-mono)', color: 'var(--wf-text2)' }}>{model.model_key}</b>?
        Essa ação remove da listagem.
      </p>
      <p style={{ fontSize: '.72rem', color: 'var(--wf-muted)', marginTop: 8 }}>
        A remoção é <b>soft</b>: o modelo vira status <code>deprecated</code> e sai do catálogo, mas o
        registro e o histórico de uso são preservados no banco.
      </p>
    </WfModalShell>
  );
}
